import { Resume } from '@sr/types';
import { GetStaticProps } from 'next';
import { NextSeo } from 'next-seo';
import { Box, Container, Text } from 'theme-ui';
import ResumeExampleComponent from 'components/examples/Example';
import ExamplesFooter from 'components/examples/Footer';
import ResumeTemplates from 'components/examples/ResumeTemplates';
import Page from 'components/layout/Page';
import ArticleJsonLd from 'components/seo/jsonld/Article';
import BreadcrumbJsonLd from 'components/seo/jsonld/Breadcrumb';
import JobsList from 'components/ui/JobsList';
import PageHero from 'components/ui/PageHero';
import RichTextGraphQl from 'components/ui/RichTextGraphQl';
import {
  JobPreviewFieldsFragment as JobPreview,
  ResumeExampleFieldsFragment as ResumeExample,
  ResumeExampleGroupFieldsFragment as ResumeExampleGroup,
} from 'graphql/generated/sdk';
import sdk from 'graphql/sdk';
import useCanonical from 'hooks/use-canonical';
import fetchResume from 'lib/fetch-resume';
import { daysAgoIsoString } from 'lib/job-utils';

type PageProps = {
  activeGroup: ResumeExampleGroup;
  examples: ResumeExample[];
  jobs: JobPreview[];
  resumes: { [key: string]: Resume };
};

export default function Examples({
  activeGroup,
  examples,
  jobs,
  resumes,
}: PageProps) {
  return (
    <>
      <Metadata activeGroup={activeGroup} />
      <Page>
        <Box as="article">
          <PageHero
            title={activeGroup.pageTitle}
            subtitle="Resume examples"
            subtitleUrl={`${process.env.NEXT_PUBLIC_WEBSITE_URL}/examples`}
            heroImageUrl={activeGroup.heroImage?.url}
            heroImageAlt={activeGroup.heroImage?.description}
            date={activeGroup.sys.publishedAt}
            authorUrl="https://standardresume.co/r/rileyt"
            authorName="Riley Tomasek"
            authorVerb="Updated"
          />
          <Container variant="slim">
            <Text variant="title" sx={{ mt: 49, mb: [64, 90] }}>
              {activeGroup.description}
            </Text>
          </Container>
          {examples.map((example, index) => (
            <ResumeExampleComponent
              example={example}
              resume={resumes[example.resumeId]}
              key={index}
            />
          ))}

          {activeGroup.body && (
            <Box
              variant="layout.gridContent"
              as="section"
              sx={{
                display: 'flex',
                flexDirection: ['column', 'row'],
                justifyContent: [null, 'center'],
                my: [100, 100, 150],
              }}
            >
              <Box
                sx={{
                  maxWidth: '70ch',
                  width: '100%',
                  '> *:first-child': { mt: 0 },
                }}
              >
                <RichTextGraphQl
                  bodyJson={activeGroup.body.json}
                  links={activeGroup.body.links}
                />
              </Box>
              {jobs && (
                <Box
                  className="vdiffignore"
                  sx={{
                    flexShrink: 0,
                    width: ['100%', 250, 300],
                    ml: [0, 32, 64, 128],
                    mt: [64, 0],
                  }}
                >
                  <JobsList
                    jobs={jobs}
                    professionTitle={activeGroup.profession.title}
                    professionSlug={activeGroup.profession.slug}
                  />
                </Box>
              )}
            </Box>
          )}

          <ResumeTemplates activeGroup={activeGroup} />

          <ExamplesFooter />
        </Box>
      </Page>
    </>
  );
}

export const getStaticProps: GetStaticProps<PageProps> = async ({ params }) => {
  const { slug } = params;
  if (Array.isArray(slug)) {
    throw new Error('Expected a single params.slug');
  }

  const [groupsData, examplesData, jobsData] = await Promise.all([
    sdk.resumeExampleGroups({
      groupSlug: slug,
      count: 1,
    }),
    sdk.resumeExamplesForGroup({
      groupSlug: slug,
    }),
    sdk.jobs({
      professionSlugs: slug,
      postedAfter: daysAgoIsoString(60),
      count: 10,
    }),
  ]);

  const activeGroup = groupsData.resumeExampleGroupCollection.items[0];
  if (!activeGroup) return { notFound: true };

  // Examples sorted by descending quality score
  const examples = examplesData.resumeExampleCollection.items.sort(
    (a, b) => b.qualityScore - a.qualityScore
  );

  // Fetch resume data from Firebase for each example
  const resumeInfo = await Promise.all(
    examples.map((example) => {
      return fetchResume({
        id: example.resumeId,
        forceProduction: true,
        removeContactInfo: true,
      });
    })
  );
  const resumes: { [key: string]: Resume } = {};
  resumeInfo.map(({ resume }) => {
    if (resume.visibility === 'secret') {
      throw new Error(`Resume ${resume.id} has visibility=secret`);
    }
    resumes[resume.id] = resume;
  });

  const jobs = jobsData.jobCollection.items;

  return {
    props: {
      activeGroup,
      examples,
      jobs,
      resumes,
    },
    revalidate: 60,
  };
};

export async function getStaticPaths() {
  const data = await sdk.resumeExampleGroupSlugs();
  const groups = data.resumeExampleGroupCollection.items;
  return {
    paths: groups.map(({ slug }) => `/examples/${slug}`) ?? [],
    fallback: 'blocking',
  };
}

function Metadata({ activeGroup }: { activeGroup: ResumeExampleGroup }) {
  const canonicalUrl = useCanonical();
  const metadata = activeGroup.seoMetadata || {};
  const ogImageUrl = `https://images.ctfassets.net/mbrzsuirfz03/4kjAf9ovwZ8pUJyfoJoxtA/cd791aca86bc6c6d568c00054a74b2ee/resume-examples-share-card.png`;
  return (
    <>
      <NextSeo
        title={metadata.title}
        description={metadata.description}
        openGraph={{
          type: 'article',
          title: metadata.ogTitle,
          article: {
            publishedTime: activeGroup.sys.firstPublishedAt,
            modifiedTime: activeGroup.sys.publishedAt,
            authors: ['https://standardresume.co/r/rileyt'],
          },
          images: [{ url: ogImageUrl }],
        }}
      />
      <ArticleJsonLd
        title={activeGroup.pageTitle}
        images={[activeGroup?.heroImage?.url]}
        datePublished={activeGroup.sys.firstPublishedAt}
        dateModified={activeGroup.sys.publishedAt}
        description={metadata.description}
      />
      <BreadcrumbJsonLd
        itemListElements={[
          {
            position: 1,
            name: 'Home',
            item: `${process.env.NEXT_PUBLIC_WEBSITE_URL}`,
          },
          {
            position: 2,
            name: 'Resume Examples',
            item: `${process.env.NEXT_PUBLIC_WEBSITE_URL}/examples`,
          },
          {
            position: 3,
            name: activeGroup.menuTitle,
            item: canonicalUrl,
          },
        ]}
      />
    </>
  );
}
